import type { MouseEvent } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  BiddingApprovalState,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  Flex,
  LightModeSemanticColors,
  OutlineDangerButton,
  PrimaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { BidMemoCell } from '~/components/bidding/BidMemoCell'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import {
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { TableMessageRow } from '~/components/common/TableMessageRow'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { isEnterKey } from '~/libs/keyboard'
import { convertAreaLabel } from '~/models/area'
import { formatJstDateTimeHm, formatJstMonthDayTime } from '~/models/dateTime'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { formatKomaLabel } from '~/models/komaLabel'
import { BiddingIntradayWithdrawStateTag } from '~/pages/IntradayBidding/components/BiddingIntradayWithdrawStateTag'
import {
  IntradayBidRowActions,
  intradayBidRowActionFlags,
} from '~/pages/IntradayBidding/components/IntradayBidRowActions'
import { IntradayBidStatusCell } from '~/pages/IntradayBidding/components/IntradayBidStatusCell'
import {
  NoValue,
  NumberOrNoValue,
  TextOrNoValue,
} from '~/pages/IntradayBidding/components/IntradayCellValue'
import { IntradayNeedsReviewChip } from '~/pages/IntradayBidding/components/IntradayNeedsReviewChip'
import {
  formatBidPrice,
  formatBidVolumeMw,
} from '~/pages/IntradayBidding/models/intradayBidFigures'
import {
  type IntradayBidRow,
  isMarketOrderRow,
  isWithdrawnBidRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'

type Props = {
  rows: IntradayBidRow[]
  /**
   * 行が 0 件の理由 (表が文言を選ぶ)。 既定 `filtered` = 絞り込みの結果が 0 件。
   * `noBids` = 受渡日に札が 1 件も無い。 `noBidsWithGuide` = 同上 + 札作成への誘導
   * (AC-BIN-016、 作成の導線が開いている時だけ呼び出し元が渡す)。
   */
  emptyState?: 'filtered' | 'noBids' | 'noBidsWithGuide'
  /** 完了/削除済みワークフロー経由で開いた場合 true。編集・取消ボタンを disabled にする。 */
  isReadonly?: boolean
  /** 取消できる行 (`row.withdrawable`) の取消ボタンの callback。 未指定なら取消ボタンを描画しない。 */
  onWithdraw?: (row: IntradayBidRow) => void
  /** 下書き行の削除 callback。 */
  onDelete?: (row: IntradayBidRow) => void
  /**
   * 下書きに戻せる行 (`row.returnableToDraft`、 送信中の札) の「下書きに戻す」 の callback
   * (AC-BIN-115)。 未指定なら描画しない。
   */
  onReturnToDraft?: (row: IntradayBidRow) => void
  /** 下書きに戻す遷移の最中か (ボタンを disabled にする)。 */
  isReturningToDraft?: boolean
  /**
   * 承認申請中行の「承認する」 操作 callback (AC-BIN-095)。 未指定なら button を描画しない。
   * 押せるかは行の `approvalActionable` (backend が状態と GC から判定、 AC-BIN-099) で決まる。
   */
  onApprove?: (row: IntradayBidRow) => void
  /**
   * 承認申請中行の「差戻し」 操作 callback (AC-BIN-095)。 未指定なら button を描画しない。
   */
  onReject?: (row: IntradayBidRow) => void
  /**
   * 承認申請中の行の操作口の近傍に常時出す注意表示 (AC-BIN-098: 承認者非該当 / 自己承認)。
   * undefined なら出さない。 button は disabled にしない。
   */
  approvalNoticeOf?: (row: IntradayBidRow) => string | undefined
  /**
   * 行クリックで詳細/編集モーダルを開く callback (AC-007)。 編集できる行 (下書きで GC 前、
   * [[intradayBidRowActionFlags]]) の編集ボタン (#10110) も同じ callback を呼ぶ。 未指定なら
   * 編集ボタンを描画しない。 行内の button クリックは stopPropagation で除外する
   * (= 取消は AC-008 の確認モーダル導線)。
   */
  onRowClick?: (row: IntradayBidRow, event?: MouseEvent) => void
  /**
   * 売買逆転 (AC-BIN-045) の対象 bid id set。 該当行を warning 系の地色で強調する
   * (8080 `.inversion-row`、 spot の `SpotBidTable` と同じ `Surface.warningLow`)。
   */
  inversionBidIds?: Set<string>
  /** 取引中断中のコマ番号 (AC-BIN-034a)。 該当行のコマ欄に「取引中断中」 バッジを出す。 */
  suspendedTimeCodes?: ReadonlySet<number>
}

/**
 * 列幅のフロア (= 下限)。 design `intraday.html` の
 * `#intraday-bid-table th/td:nth-child(n)` (2026-08-14 / #5362) の実測値をそのまま写す。
 *
 * この表は auto layout なので列幅が「今表示されている行の内容」で決まり、 売/買 等の
 * 絞り込みで列幅が動く (#5362。 design 実測で 7 発注主体中 6 つで 売/買 の列ベクトルが
 * 不一致だった)。 各列に下限を与えて内容非依存にする。 `width` ではなく `minWidth` を
 * 使う理由・th と td の両方に置く理由は `SpotBidTable` の同名定数のコメントを参照。
 */
const COL_MIN_WIDTH = {
  /** コマ / 時間帯。 */
  koma: '181px',
  /** GC締切。 */
  gcDeadline: '101px',
  /** エリア。 */
  area: '92px',
  /** 売/買。 */
  side: '86px',
  /** 入札量 (MW)。 */
  bidVolume: '90px',
  /** 約定価格 (円/kWh、 AC-BIN-003)。 未約定は空。 */
  matchedPrice: '110px',
  /** 約定量 (MW) = design「約定量 (MWh)」列。 */
  execVolume: '100px',
  /** 残数量 (MW、 AC-BIN-003)。 */
  remainingVolume: '100px',
  /** 入札価格 (円/kWh)。 */
  bidPrice: '116px',
  /** 取下予定日時 (AC-BIN-003)。 */
  withdrawAt: '116px',
  /** 受渡契約コード (AC-BIN-003)。 */
  deliveryContractCd: '140px',
  /** ワークフローID (AC-BIN-003)。 */
  workflowId: '140px',
  /** JEPX自由記述欄 / メモ (AC-BIN-003)。 SpotBidTable の同名列と同じ 180px 省略表示。 */
  memoJepx: '180px',
  memoFree: '180px',
  /** JEPX入札番号 (AC-BIN-003)。 値の無い札は「—」。 */
  jepxBidNo: '120px',
  /** 最終更新 (AC-BIN-003)。 */
  updatedAt: '140px',
  /**
   * ステータス。 AC-BIN-035 で業務ステータス + 承認ステータスを 1 列に統合した
   * (T-56b) 後も、 design `intraday.html` のステータス列 (`min-width: 120px`) の
   * 実測値のまま。
   */
  status: '120px',
  /**
   * 操作 (編集・取消のアイコンボタン、 承認申請中行は「差戻し」「承認」 ボタンが並ぶ AC-BIN-095)。
   * 2 button 並びを折り返さず収める幅。 表のセル内のボタンは md (高さ 40px・padding 16px) で、
   * 最も広い並びは承認申請中行の「差戻し」(76px) + 「承認する」(88px) = 76 + 4 (gap) + 88 = 168px、
   * 固定列の左右 padding 26px を足して 194px (実測で 192px だと折り返したので 196px)。 アイコン 2 つ (編集・取消) は 40 + 8 + 40 + 26 = 114px。
   */
  actions: '196px',
} as const

/**
 * 取下予定日時セルの表示文字列 (AC-BIN-003)。 予定が無ければ undefined。 「表示形式は
 * MM/dd HH:mm とし、 当日分と翌日分を判別できるよう日付を必ず併記する」 (spec) ので
 * `formatJstMonthDayTime` を使う (= 監査情報の yyyy/MM/dd HH:mm とは別書式、
 * `models/dateTime.ts` の doc comment 参照)。
 */
const withdrawAtLabel = (row: IntradayBidRow): string | undefined =>
  row.withdrawAt !== undefined
    ? formatJstMonthDayTime(timestampDate(row.withdrawAt))
    : undefined

/**
 * 最終更新セル (AC-BIN-003)。 セル自体は 更新日時 (yyyy/MM/dd HH:mm、 AC-BIN-047 と
 * 同じ軸・書式) と更新者を表示し、 ホバー tooltip で 作成者・作成日時 + 更新者・更新日時
 * の詳細を出す (TC-BIN-D-03.1)。 member ID → 表示名の解決は `useMemberDisplayName`
 * (解決できなければ「—」)。
 */
const LastUpdatedCell = ({
  row,
}: {
  row: IntradayBidRow
}): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()

  if (row.updatedAt === undefined) {
    return <NoValue />
  }

  const updatedByName = resolveDisplayName(row.updatedBy) ?? '—'
  const updatedAtLabel = formatJstDateTimeHm(timestampDate(row.updatedAt))
  const createdByName = resolveDisplayName(row.createdBy) ?? '—'
  const createdAtLabel =
    row.createdAt !== undefined
      ? formatJstDateTimeHm(timestampDate(row.createdAt))
      : '—'

  return (
    <Tooltip
      content={
        <Box>
          <Box>
            {t('page:biddingIntraday.column.audit.createdBy')}: {createdByName}
          </Box>
          <Box>
            {t('page:biddingIntraday.column.audit.createdAt')}: {createdAtLabel}
          </Box>
          <Box>
            {t('page:biddingIntraday.column.audit.updatedBy')}: {updatedByName}
          </Box>
          <Box>
            {t('page:biddingIntraday.column.audit.updatedAt')}: {updatedAtLabel}
          </Box>
        </Box>
      }
    >
      <Box>
        <Box fontSize="sm">{updatedAtLabel}</Box>
        <Box color="Text.lowEmphasis" fontSize="xs">
          {updatedByName}
        </Box>
      </Box>
    </Tooltip>
  )
}

/**
 * 入札一覧テーブル。 design HTML `#intraday-bid-table` の列構成をベースに、 AC-BIN-003
 * が定める列を追加する。
 *
 * 列: ステータス(= 業務+承認の統合、 AC-BIN-035) / コマ/時間帯 / GC締切 / エリア / 売買 /
 *     入札量(MW) / 約定価格(円/kWh) / 約定量(MW) / 残数量(MW) / 入札価格(円/kWh) /
 *     取下予定日時 / 受渡契約コード / ワークフローID / JEPX自由記述欄 / メモ /
 *     JEPX入札番号 / 最終更新 / 操作(編集・取消、 承認申請中行は承認する/差戻し AC-BIN-095)。
 * 独立した「タグ」「承認」列は持たない (「タグ」は spec #2549 で廃止、 「承認」は
 * AC-BIN-003 / AC-BIN-035 でステータス列に統合、 T-56b で撤去)。
 * 取下 (= CANCELLED) 行は opacity を落とす。 編集・取消ボタンの出し方は板情報の自社入札と
 * 共通の [[IntradayBidRowActions]] (#10110。 編集 = 下書きで GC 前、 取消 = backend が取消
 * できると返した行 `row.withdrawable`、 AC-BIN-008 / AC-BIN-093)。 「承認する」「差戻し」 は
 * 承認申請中の行に出し、 backend が承認・差戻しを受け付けないと返した行
 * (`row.approvalActionable` = false。 GC 経過、 AC-BIN-099) では無効化する。 どちらも
 * GC を過ぎた行は、 親が一覧を取り直した時に値が変わる (取り直すまでの間に押された承認・
 * 差戻しは、 確定直前の GC 判定 `useIntradayApprovalDecision` が止める)。
 */
const EMPTY_STATE_KEYS = {
  filtered: 'page:biddingIntraday.bidTable.empty',
  noBids: 'page:biddingIntraday.bidTable.noBids',
  noBidsWithGuide: 'page:biddingIntraday.bidTable.noBidsGuide',
} as const

export const IntradayBidTable = ({
  rows,
  emptyState = 'filtered',
  isReadonly = false,
  onWithdraw,
  onDelete,
  onReturnToDraft,
  isReturningToDraft = false,
  onApprove,
  onReject,
  approvalNoticeOf,
  onRowClick,
  inversionBidIds,
  suspendedTimeCodes,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const now = useNowByMinute()
  // 読み取り専用で非活性にした取下・差戻し・承認の理由 (AC-WFW-014)。承認締切 (isApprovalClosed)
  // など画面固有の理由より優先して単独で出す。
  const { readonlyReason } = useWorkflowLockReasons()
  // 操作列 (編集・取消・承認する・差戻し) を右端に固定する。 18 列で横スクロールが常態のため、
  // 固定しないと承認待ち行の「承認する」 が画面の外に出る (#12635。 共通 hook / UI 辞書 table.md §1)。
  const { wrapRef, stickyShadow } = useStickyRightShadow([rows])

  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。 コマが飛ぶ・
  // 1 コマが複数行の一覧なので、 境目をまたぐ行の下に 1 本だけ引く。
  const komaDividerFlags = komaListDividerFlags(rows, (row) => row.timeCode)
  return (
    // container-type は空状態の行 (TableMessageRow) が横スクロールしても見えている幅の
    // 中央に文言を置くために要る (100cqi の基準)。
    <Box ref={wrapRef} overflowX="auto" containerType="inline-size">
      {/* design `.ecl-table` td = font-size md (14px)。 ecloud table size="sm" は
          textStyle sm (12px) で一段小さいため、 root に fontSize="md" を当てて
          td/th へ継承させる (= 共通 MasterDataTable と同じ 14px に揃える)。 */}
      <TableRoot fontSize="md" size="sm" variant="line">
        <TableHeader
          position="sticky"
          // 右端固定の操作列セル (zIndex 2) より上に置く (縦スクロールで見出しが隠れない)。
          zIndex={3}
          top={0}
          backgroundColor="Surface.primary"
        >
          <TableRow>
            {/* [design 差異] ステータスは全画面共通で列の先頭に置く
                (2026-08-27 デザイナー判断)。 */}
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.status}
              textAlign="center"
            >
              {t('page:biddingIntraday.column.status')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.koma}>
              {t('page:biddingIntraday.column.komaTimeBand')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.gcDeadline}>
              {t('page:biddingIntraday.column.gcDeadline')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.area}>
              {t('page:biddingIntraday.column.area')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.side} textAlign="center">
              {t('page:biddingIntraday.column.side')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.bidVolume}
              textAlign="end"
            >
              {t('page:biddingIntraday.column.bidVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.matchedPrice}
              textAlign="end"
            >
              {t('page:biddingIntraday.column.matchedPriceYenPerKwh')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.execVolume}
              textAlign="end"
            >
              {t('page:biddingIntraday.column.matchedVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.remainingVolume}
              textAlign="end"
            >
              {t('page:biddingIntraday.column.remainingVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.bidPrice}
              textAlign="end"
            >
              {t('page:biddingIntraday.column.bidPriceYenPerKwh')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.withdrawAt}>
              {t('page:biddingIntraday.column.withdrawAt')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.deliveryContractCd}>
              {t('page:biddingIntraday.column.deliveryContractCd')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.workflowId}>
              {t('page:biddingIntraday.column.workflowId')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.memoJepx}>
              {t('page:biddingIntraday.column.memoJepx')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.memoFree}>
              {t('page:biddingIntraday.column.memo')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.jepxBidNo}>
              {t('page:biddingIntraday.column.jepxBidNo')}
            </TableColumnHeader>
            <TableColumnHeader minWidth={COL_MIN_WIDTH.updatedAt}>
              {t('page:biddingIntraday.column.updatedAt')}
            </TableColumnHeader>
            <TableColumnHeader
              minWidth={COL_MIN_WIDTH.actions}
              textAlign="end"
              aria-label={t('common:table.actionsLabel')}
              data-sticky-shadow={stickyShadow}
              // 見出しは sticky top と sticky right が交わる角 (z=3)。
              style={stickyActionHeaderStyle(TABLE_HEADER_BG)}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* 空状態はヘッダー直下の 1 行 (UI 辞書 table.md §3: 高さ 88px 下限 / mid-emphasis /
              横スクロールしても見えている幅の中央)。 */}
          {rows.length === 0 && (
            <TableMessageRow
              colSpan={18}
              message={t(EMPTY_STATE_KEYS[emptyState])}
            />
          )}
          {rows.map((row, rowIndex) => {
            // design は取消の行だけを薄く描き、 自動取消の行は薄くしない (AC-BIN-035)。
            const isDimmed =
              isWithdrawnBidRow(row) &&
              row.bidStatus !== BiddingIntradayOrderStatus.AUTO_CANCELLED
            // AC-BIN-095 / AC-BIN-003: 承認申請中の行にだけ操作列へ「承認する」「差戻し」を出す。
            // callback 未指定の呼び出し元 (= 承認導線を配線しない文脈) では押しても何も
            // 起きない button を出さないよう、 描画自体を callback の有無に揃える。
            const isPendingApproval =
              row.approvalState === BiddingApprovalState.PENDING
            const showApprove = isPendingApproval && onApprove !== undefined
            const showReject = isPendingApproval && onReject !== undefined
            // AC-BIN-099: 承認・差戻しを受け付けない行 (GC 経過) は disabled にする。 判定は
            // backend (`approvalActionable`) が持つ。
            const isApprovalClosed = !row.approvalActionable
            const approvalNotice =
              showApprove || showReject ? approvalNoticeOf?.(row) : undefined
            // #10110: 編集・取消は callback を配線した文脈でだけ出す (承認系と同じ考え方)。
            const flags = intradayBidRowActionFlags(row, now)
            const rowActionFlags = {
              edit: flags.edit && onRowClick !== undefined,
              withdraw: flags.withdraw && onWithdraw !== undefined,
              deleteDraft: flags.deleteDraft && onDelete !== undefined,
              returnToDraft:
                flags.returnToDraft && onReturnToDraft !== undefined,
            }
            const hasRowAction =
              rowActionFlags.edit ||
              rowActionFlags.withdraw ||
              rowActionFlags.deleteDraft ||
              rowActionFlags.returnToDraft
            return (
              <TableRow
                key={row.id}
                opacity={isDimmed ? 0.6 : 1}
                _hover={
                  onRowClick !== undefined
                    ? {
                        backgroundColor: 'Surface.secondary',
                        // 固定した操作列セルにも同じ地を重ねる (透過する行背景を継承できないため)。
                        '& [data-sticky-action]': {
                          '--sticky-overlay':
                            LightModeSemanticColors.Surface.secondary.value,
                        },
                      }
                    : undefined
                }
                cursor={onRowClick !== undefined ? 'pointer' : undefined}
                backgroundColor={
                  inversionBidIds?.has(row.id) === true
                    ? 'Surface.warningLow'
                    : undefined
                }
                css={
                  komaDividerFlags[rowIndex] === true
                    ? KOMA_DIVIDER_ROW_CSS
                    : undefined
                }
                onClick={
                  onRowClick !== undefined
                    ? (e?: MouseEvent) => {
                        onRowClick(row, e)
                      }
                    : undefined
                }
                // AC-007: 詳細導線をキーボードでも辿れるようにする (行を
                // フォーカス可能にし Enter/Space で同じ callback を発火)。
                // 行内ボタン (取下) から bubble した keydown は無視する
                // (= 行自身がフォーカス元のときだけ処理。ボタンの Enter/Space を
                // preventDefault で潰したり詳細モーダルを二重に開かない)。
                onKeyDown={
                  onRowClick !== undefined
                    ? (e) => {
                        if (e.target !== e.currentTarget) return
                        if (isEnterKey(e) || e.key === ' ') {
                          e.preventDefault()
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
                role={onRowClick !== undefined ? 'button' : undefined}
                tabIndex={onRowClick !== undefined ? 0 : undefined}
              >
                <TableCell minWidth={COL_MIN_WIDTH.status} textAlign="center">
                  <IntradayBidStatusCell bid={row} />
                  {/* AC-BIN-008b: 取消処理中・取消失敗はステータスバッジと別軸のチップで併記 (取消失敗は札が終わった後も残す)。 */}
                  <BiddingIntradayWithdrawStateTag
                    failureReason={row.withdrawFailureReason}
                    state={row.withdrawState}
                  />
                  {/* AC-BIN-127: ステータスバッジ → 取消状態のタグ → 要確認の順 (design intraday.html)。 */}
                  <IntradayNeedsReviewChip needsReview={row.needsReview} />
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.koma} whiteSpace="nowrap">
                  {formatKomaLabel(t, row.timeCode)}
                  {suspendedTimeCodes?.has(row.timeCode) === true && (
                    <Badge marginLeft={1} status="warning">
                      {t('page:biddingIntraday.market.suspended')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.gcDeadline}
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {row.gcDeadline}
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.area}>
                  {convertAreaLabel(row.areaCode) || '—'}
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.side} textAlign="center">
                  <TradeSideLabel side={row.direction} />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.bidVolume}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatBidVolumeMw(row.volumeMw)}
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.matchedPrice}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue
                    digits={2}
                    value={row.matchedPriceYenPerKwh}
                  />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.execVolume}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue digits={1} value={row.execVolumeMw} />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.remainingVolume}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue digits={1} value={row.remainingVolumeMw} />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.bidPrice}
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {/* 成行注文は価格を持たない (AC-BIN-004d)。 内部表現の "0" を
                      そのまま出すと 0 円の指値としか見えず区別できない。 */}
                  {isMarketOrderRow(row)
                    ? t('page:biddingIntraday.bidTable.marketOrderPrice')
                    : formatBidPrice(row.priceYenPerKwh)}
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.withdrawAt}
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <TextOrNoValue value={withdrawAtLabel(row)} />
                </TableCell>
                {/* 受渡契約コード: 作成時に保存したコード、 取り込んだ札 (AC-BIN-106) は外部のコード。
                    保存前の旧い注文は未設定 (「—」表示)。 */}
                <TableCell minWidth={COL_MIN_WIDTH.deliveryContractCd}>
                  <TextOrNoValue value={row.deliveryContractCd} />
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.workflowId}>
                  <TextOrNoValue value={row.workflowId} />
                </TableCell>
                <BidMemoCell
                  width={COL_MIN_WIDTH.memoJepx}
                  value={row.memoJepx}
                />
                <BidMemoCell
                  width={COL_MIN_WIDTH.memoFree}
                  value={row.memoFree}
                />
                <TableCell minWidth={COL_MIN_WIDTH.jepxBidNo}>
                  <TextOrNoValue value={row.jepxBidNo} />
                </TableCell>
                <TableCell minWidth={COL_MIN_WIDTH.updatedAt}>
                  <LastUpdatedCell row={row} />
                </TableCell>
                <TableCell
                  minWidth={COL_MIN_WIDTH.actions}
                  textAlign="end"
                  whiteSpace="nowrap"
                  data-sticky-action
                  data-sticky-shadow={stickyShadow}
                  // 縞の無い表なので index 0 (= 地は白)。 売買逆転の行は行と同じ地にする。
                  style={{
                    ...stickyActionCellStyle(0),
                    ...(inversionBidIds?.has(row.id) === true
                      ? {
                          ['--sticky-fill' as string]:
                            LightModeSemanticColors.Surface.warningLow.value,
                        }
                      : {}),
                  }}
                >
                  {hasRowAction || showApprove || showReject ? (
                    <Flex
                      alignItems="flex-end"
                      flexDirection="column"
                      gap={1}
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <Flex justifyContent="flex-end" flexWrap="wrap" gap={1}>
                        {hasRowAction && (
                          <IntradayBidRowActions
                            row={row}
                            flags={rowActionFlags}
                            isReadonly={isReadonly}
                            isReturningToDraft={isReturningToDraft}
                            onDelete={(r) => onDelete?.(r)}
                            onEdit={(r) => onRowClick?.(r)}
                            onReturnToDraft={(r) => onReturnToDraft?.(r)}
                            onWithdraw={(r) => onWithdraw?.(r)}
                          />
                        )}
                        {/* AC-BIN-095: 承認申請中の行だけに出す。 AC-BIN-099 で GC 経過後は
                          disabled にする。 承認者非該当 ・ 自己承認 (AC-BIN-098) でも
                          disabled にせず、 下の注意表示と確認モーダルで警告する。 */}
                        {showReject && (
                          <ButtonTooltip content={readonlyReason}>
                            <OutlineDangerButton
                              disabled={isReadonly || isApprovalClosed}
                              onClick={() => onReject(row)}
                              size="md"
                            >
                              {t('page:biddingIntraday.action.reject')}
                            </OutlineDangerButton>
                          </ButtonTooltip>
                        )}
                        {showApprove && (
                          <ButtonTooltip content={readonlyReason}>
                            <PrimaryButton
                              disabled={isReadonly || isApprovalClosed}
                              onClick={() => onApprove(row)}
                              size="md"
                            >
                              {t('page:biddingIntraday.action.approve')}
                            </PrimaryButton>
                          </ButtonTooltip>
                        )}
                      </Flex>
                      {approvalNotice !== undefined && (
                        <Text
                          color="Text.warningHigh"
                          fontSize="xs"
                          textAlign="left"
                          whiteSpace="normal"
                        >
                          {approvalNotice}
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    <NoValue />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
